/**
 * Start mask — full-screen overlay shown before the player enters the
 * 3D world.  Asks for a name (Issue #8) and a chat-style preview.
 */

import { MAX_NAME_LEN } from '@hurka/shared';

export interface NameMaskHandle {
  show(): void;
  hide(): void;
  /** Resolves with the chosen name, or null if dismissed. */
  waitForName(): Promise<string | null>;
}

export function createNameMask(): NameMaskHandle {
  const root = document.createElement('div');
  root.id = 'name-mask';
  Object.assign(root.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(0,0,0,0.85)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '100',
    fontFamily: '"Courier New", monospace',
    color: '#c0c0c0',
  } as CSSStyleDeclaration);

  root.innerHTML = `
    <div style="
      background: linear-gradient(180deg, #1a1612 0%, #0a0808 100%);
      border: 2px solid #c0a060;
      padding: 32px 40px;
      border-radius: 4px;
      box-shadow: 0 0 40px rgba(192,160,96,0.3);
      min-width: 380px;
      text-align: center;
    ">
      <h1 style="font-size:42px;color:#c0a060;letter-spacing:8px;margin:0 0 8px 0;text-shadow: 2px 2px 0 #000;">HURKA</h1>
      <p style="color:#7a6a4a;margin:0 0 24px 0;font-size:13px;">— Browser 3D Multiplayer RPG —</p>
      <label for="name-input" style="display:block;color:#c0a060;margin-bottom:6px;">Choose your name</label>
      <input id="name-input" type="text" maxlength="${MAX_NAME_LEN}"
             style="width:100%;padding:10px;background:#000;border:1px solid #5a4a30;color:#fff;font-family:inherit;font-size:16px;text-align:center;"
             placeholder="Anonymous Wanderer" />
      <button id="name-submit" type="button"
              style="margin-top:18px;width:100%;padding:10px;background:#5a3e22;color:#fff;border:2px solid #c0a060;font-family:inherit;font-size:14px;letter-spacing:2px;cursor:pointer;">
        ENTER THE REALM
      </button>
      <p style="margin-top:18px;color:#5a4a30;font-size:11px;">
        WASD = move &nbsp;·&nbsp; Mouse = look &nbsp;·&nbsp; Click = attack &nbsp;·&nbsp; E = pickup &nbsp;·&nbsp; I = inventory &nbsp;·&nbsp; Enter = chat
      </p>
    </div>
  `;
  document.body.appendChild(root);

  let resolver: ((v: string | null) => void) | null = null;
  let dismissed = false;

  function submit(): void {
    if (dismissed) return;
    const input = root.querySelector('#name-input') as HTMLInputElement | null;
    const name = (input?.value ?? '').trim() || `Wanderer${Math.floor(Math.random() * 9000 + 1000)}`;
    dismissed = true;
    resolver?.(name.slice(0, MAX_NAME_LEN));
    root.remove();
  }

  root.querySelector('#name-submit')?.addEventListener('click', submit);
  root.querySelector('#name-input')?.addEventListener('keydown', (e) => {
    const ke = e as KeyboardEvent;
    if (ke.key === 'Enter') submit();
  });

  return {
    show() {
      root.style.display = 'flex';
      (root.querySelector('#name-input') as HTMLInputElement | null)?.focus();
    },
    hide() {
      root.remove();
    },
    waitForName() {
      return new Promise((res) => {
        resolver = res;
        (root.querySelector('#name-input') as HTMLInputElement | null)?.focus();
      });
    },
  };
}
